import React, { useState, useEffect } from 'react';
import { FileText, Upload, Loader2, TrendingUp, AlertCircle, Star, User } from 'lucide-react';
import { Button } from '../ui/button';

const ResumeScreening = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setAnalysis(null);
      setError('');
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile || !selectedJob || !candidateName || !candidateEmail) {
      setError('Please fill all fields and select a resume');
      return;
    }

    setIsAnalyzing(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('resume', selectedFile);
      formData.append('job_id', selectedJob);
      formData.append('candidate_name', candidateName);
      formData.append('candidate_email', candidateEmail);

      const token = localStorage.getItem('shansoats_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shansoats/screen-resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
        // Reset form
        setSelectedFile(null);
        setCandidateName('');
        setCandidateEmail('');
        setSelectedJob('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Analysis failed');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-8 h-8 text-cyan-400" />
        <div>
          <h2 className="text-3xl font-bold text-white">AI Resume Screening</h2>
          <p className="text-gray-400">Upload resumes for AI-powered analysis</p>
        </div>
      </div>

      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-white mb-2">Select Job *</label>
          <select
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
          >
            <option value="">-- Select a job --</option>
            {jobs.map(job => (
              <option key={job.id} value={job.id}>{job.title} - {job.department}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Candidate Name *</label>
            <input
              type="text"
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              placeholder="John Doe"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Candidate Email *</label>
            <input
              type="email"
              value={candidateEmail}
              onChange={(e) => setCandidateEmail(e.target.value)}
              placeholder="john@example.com"
              className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-white mb-2">Upload Resume *</label>
          {selectedFile ? (
            <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-cyan-400" />
                <span className="text-white">{selectedFile.name}</span>
              </div>
              <button
                onClick={() => setSelectedFile(null)}
                className="text-red-400 hover:text-red-300"
              >
                Remove
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-400">Click to upload or drag and drop</p>
              <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX (MAX. 5MB)</p>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        <Button
          onClick={handleAnalyze}
          disabled={isAnalyzing || !selectedFile || !selectedJob || !candidateName || !candidateEmail}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 py-4 text-lg"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Analyzing Resume...
            </>
          ) : (
            <>
              <TrendingUp className="w-5 h-5 mr-2" />
              Analyze Resume
            </>
          )}
        </Button>
      </div>

      {analysis && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">Analysis Results</h3>
            <div className="flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-400" />
              <span className="text-3xl font-bold text-white">{analysis.match_score}/100</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-sm text-blue-400 mb-1">Experience</p>
              <p className="text-xl font-bold text-white">{analysis.experience || 'N/A'}</p>
            </div>
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
              <p className="text-sm text-cyan-400 mb-1">Skills Match</p>
              <p className="text-xl font-bold text-white">{analysis.skills_match || 'N/A'}</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-sm text-green-400 mb-1">Recommendation</p>
              <p className="text-xl font-bold text-white">{analysis.recommendation || 'Review'}</p>
            </div>
          </div>

          {analysis.key_skills && analysis.key_skills.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-3">Key Skills Detected</h4>
              <div className="flex flex-wrap gap-2">
                {analysis.key_skills.map((skill, idx) => (
                  <span key={idx} className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-sm text-cyan-400">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {analysis.summary && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-3">AI Summary</h4>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-gray-300 whitespace-pre-line">{analysis.summary}</p>
              </div>
            </div>
          )}

          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <p className="text-green-400 font-semibold flex items-center gap-2">
              <User className="w-5 h-5" />
              Candidate added to pipeline automatically
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeScreening;
